/**
 * Call Processing Admin Page
 * 
 * Allows manual triggering of call processing and management of automated jobs.
 * Features:
 * - Manual processing for date ranges
 * - View processing job history
 * - Pause/resume automated daily processing
 * - Real-time progress tracking
 */

import { useState } from 'react';
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
// Select components removed - agent type is now fixed for this portal
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Play, 
  Pause, 
  RefreshCw, 
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Phone,
  FileText,
  ThumbsUp,
  AlertTriangle,
  Settings,
  History
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { getRetellAgentIdsForOrg } from "../../../shared/agentConfig";
import { useOrgInfo } from "@/hooks/useOrganization";


export default function CallProcessing() {
  // Get user's organization for agent filtering
  const { orgSlug, orgName } = useOrgInfo();
  
  // Get the Retell agent IDs for this organization
  const agentIds = getRetellAgentIdsForOrg(orgSlug);
  
  // Form state
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  // Agent type is fixed to 'welcome' for DigniFi portal
  // Settlement Agent has its own separate portal deployment
  const agentType: 'welcome' | 'settlement' = 'welcome';
  const [isAutomatedEnabled, setIsAutomatedEnabled] = useState(true);
  
  // Fetch calls preview - filtered by organization's agent IDs
  const { 
    data: callsPreview, 
    isLoading: previewLoading,
    refetch: refetchPreview
  } = trpc.callProcessing.fetchCalls.useQuery({
    startDate,
    endDate,
    minDurationMs: 60000,
    agentIds: agentIds.length > 0 ? agentIds : undefined,
  }, {
    enabled: !!startDate && !!endDate,
  });

  // Get job history
  const { 
    data: jobHistory, 
    isLoading: historyLoading,
    refetch: refetchHistory
  } = trpc.callProcessing.getJobStatus.useQuery({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date().toISOString(),
  });

  // Run processing mutation
  const runProcessing = trpc.callProcessing.runProcessing.useMutation({
    onSuccess: (data) => {
      alert(`Processing Complete!\n\nProcessed ${data.stats.callsTranscribed} calls.\nFound ${data.stats.positiveFeedbackFound} positive feedback.\nFound ${data.stats.complianceIssuesFound} compliance issues.`);
      refetchHistory();
    },
    onError: (error) => {
      alert(`Processing Failed: ${error.message}`);
    },
  });

  const handleRunProcessing = () => {
    runProcessing.mutate({
      startDate,
      endDate,
      agentType,
    });
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/50">Completed</Badge>;
      case 'running':
        return <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/50">Running</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-500 border-red-500/50">Failed</Badge>;
      case 'cancelled':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/50">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Call Processing</h1>
            <p className="text-muted-foreground mt-1">
              Process call recordings through AssemblyAI for transcription and classification
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetchPreview();
              refetchHistory();
            }}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Manual Processing */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="h-5 w-5" />
                Manual Processing
              </CardTitle>
              <CardDescription>
                Process calls for a specific date range
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Agent Type</Label>
                  <div className="flex items-center h-10 px-3 rounded-md border border-input bg-muted/50">
                    <span className="text-sm">Welcome Agent (DigniFi)</span>
                  </div>
                </div>
              </div>

              {/* Preview */}
              {previewLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : callsPreview && (
                <div className="p-4 bg-muted/30 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Calls Found</span>
                    <span className="text-2xl font-bold">{callsPreview.filteredCalls}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Total calls in range</span>
                    <span>{callsPreview.totalCalls}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>Filtered (≥1 min duration)</span>
                    <span>{callsPreview.filteredCalls}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>With recordings</span>
                    <span>{callsPreview.calls.filter(c => c.hasRecording).length}</span>
                  </div>
                </div>
              )}

              <Button 
                onClick={handleRunProcessing}
                disabled={runProcessing.isPending || !callsPreview?.filteredCalls}
                className="w-full"
              >
                {runProcessing.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Process {callsPreview?.filteredCalls || 0} Calls
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Automated Processing Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Automated Processing
              </CardTitle>
              <CardDescription>
                Daily processing runs at 11:00 PM
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Daily Processing</Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically process yesterday's calls
                  </p>
                </div>
                <Switch
                  checked={isAutomatedEnabled}
                  onCheckedChange={setIsAutomatedEnabled}
                />
              </div>

              <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Schedule: Daily at 11:00 PM</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Processes: Previous day's calls</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">Filter: Calls ≥ 1 minute</span>
                </div>
              </div>

              <div className="pt-2">
                <Badge 
                  variant={isAutomatedEnabled ? "default" : "secondary"}
                  className={isAutomatedEnabled ? "bg-green-500" : ""}
                >
                  {isAutomatedEnabled ? "Active" : "Paused"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Processing Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Calls Processed</CardTitle>
              <Phone className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {jobHistory?.reduce((sum, j) => sum + (j.callsTranscribed || 0), 0) || 0}
              </div>
              <p className="text-xs text-muted-foreground">Last 30 days</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Positive Feedback</CardTitle>
              <ThumbsUp className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">
                {jobHistory?.reduce((sum, j) => sum + (j.positiveFeedbackFound || 0), 0) || 0}
              </div>
              <p className="text-xs text-muted-foreground">Detected</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Compliance Issues</CardTitle>
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-500">
                {jobHistory?.reduce((sum, j) => sum + (j.complianceIssuesFound || 0), 0) || 0}
              </div>
              <p className="text-xs text-muted-foreground">Flagged</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-500">
                {jobHistory?.length ? 
                  Math.round((jobHistory.filter(j => j.status === 'completed').length / jobHistory.length) * 100) 
                  : 0}%
              </div>
              <p className="text-xs text-muted-foreground">Jobs completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Job History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Processing History
            </CardTitle>
            <CardDescription>
              Recent call processing jobs
            </CardDescription>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : jobHistory && jobHistory.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date Range</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                    <TableHead className="text-right">Positive</TableHead>
                    <TableHead className="text-right">Compliance</TableHead>
                    <TableHead className="text-right">Errors</TableHead>
                    <TableHead className="text-right">Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobHistory.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {job.startDate ? new Date(job.startDate).toLocaleDateString() : '-'}
                            {job.endDate && job.startDate !== job.endDate && (
                              <> - {new Date(job.endDate).toLocaleDateString()}</>
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{job.jobType}</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(job.status)}</TableCell>
                      <TableCell className="text-right">{job.callsTranscribed || 0}</TableCell>
                      <TableCell className="text-right text-green-500">{job.positiveFeedbackFound || 0}</TableCell>
                      <TableCell className="text-right text-yellow-500">{job.complianceIssuesFound || 0}</TableCell>
                      <TableCell className="text-right text-red-500">{job.errorsEncountered || 0}</TableCell>
                      <TableCell className="text-right">{formatDuration(job.durationSeconds)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No processing jobs found.</p>
                <p className="text-sm">Run your first processing job above.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
